import React, {useEffect, useRef, useState} from "react";
import "./Progress.scss";
import {illustration, techStack} from "../../portfolio";
import Reveal from "../../components/reveal/Reveal";
import DisplayLottie from "../../components/displayLottie/DisplayLottie";

function parsePercent(value) {
  return parseInt(String(value).replace("%", ""), 10) || 0;
}

function AnimatedPercent({target, active}) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) {
      setValue(0);
      return;
    }
    let frame;
    const duration = 1100;
    const start = performance.now();

    const tick = now => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, target]);

  return <span className="skill-percent">{value}%</span>;
}

export default function StackProgress() {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [lottieData, setLottieData] = useState(null);

  useEffect(() => {
    if (!illustration.animated) return undefined;
    let cancelled = false;
    import("../../assets/lottie/build").then(mod => {
      if (!cancelled) setLottieData(mod.default);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      {threshold: 0.25}
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (!techStack.viewSkillBars) {
    return null;
  }

  return (
    <div
      ref={sectionRef}
      className={`main proficiency-section${inView ? " is-inview" : ""}`}
      id="proficiency"
    >
      <Reveal direction="up">
        <div className="proficiency-content reveal-stagger">
          <header className="proficiency-header" style={{"--i": 0}}>
            <span className="section-eyebrow">Proficiency</span>
            <h1 className="section-heading">Technical depth</h1>
            <p className="section-subtitle">
              Where I spend most of my time building and shipping
            </p>
          </header>

          <div className="proficiency-layout" style={{"--i": 1}}>
          <div className="skills-bar">
            {techStack.experience.map((exp, i) => {
              const target = parsePercent(exp.progressPercentage);
              return (
                <div key={exp.Stack} className="skill" style={{"--i": i}}>
                  <div className="skill-label">
                    <span>{exp.Stack}</span>
                    <AnimatedPercent target={target} active={inView} />
                  </div>
                  <div className="meter">
                    <span
                      style={{
                        width: exp.progressPercentage,
                        animationDelay: `${0.15 + i * 0.12}s`
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="proficiency-lottie-wrap">
            <div className="lottie-frame">
              {illustration.animated && lottieData ? (
                <DisplayLottie animationData={lottieData} speed={2} />
              ) : illustration.animated ? (
                <div className="lottie-skeleton" aria-hidden="true" />
              ) : (
                <img
                  alt="Skills"
                  src={require("../../assets/images/skill.svg")}
                />
              )}
            </div>
            <div className="proficiency-chips" aria-hidden="true">
              <span>Nuxt</span>
              <span>Stripe</span>
              <span>AWS</span>
            </div>
          </div>
        </div>
        </div>
      </Reveal>
    </div>
  );
}
